import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "./index.css";

import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { MantineProvider, Center, Loader } from "@mantine/core";
import theme from "./utils/theme";
import { ModalsProvider } from "@mantine/modals";
import { ClerkProvider } from "@clerk/clerk-react";
import { Notifications } from "@mantine/notifications";
import StoreProvider from "./StoreProvider";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { DashboardLayout } from "./components/DashboardLayout/DashboardLayout";
import { SentryUserTracker } from "./components/SentryUserTracker";

// Page imports
const ContentTable = lazy(() => import("./features/ContentTable"));
const Member = lazy(() => import("./features/Member"));
const Dashboard = lazy(() => import("./features/Dashboard"));
const Workspace = lazy(() => import("./features/Workspace"));
const Profile = lazy(() => import("./features/Profile"));
const Login = lazy(() =>
  import("./features/Auth").then((m) => ({ default: m.Login })),
);
const Register = lazy(() =>
  import("./features/Auth").then((m) => ({ default: m.Register })),
);
const OAuth = lazy(() => import("./features/OAuth"));
const Setting = lazy(() => import("./features/Setting"));
const AcceptInvite = lazy(
  () => import("./features/Member/components/AcceptInvite"),
);
const ConnectChannel = lazy(
  () => import("./features/Workspace/components/ConnectChannel"),
);
const Content = lazy(() => import("./features/Content"));
const Inbox = lazy(() => import("./features/Inbox/Inbox"));

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Publishable Key");
}

function App() {
  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      signInUrl={import.meta.env.VITE_CLERK_SIGN_IN_URL}
      signUpUrl={import.meta.env.VITE_CLERK_SIGN_UP_URL}
      signInFallbackRedirectUrl={
        import.meta.env.VITE_CLERK_SIGN_IN_FALLBACK_REDIRECT_URL
      }
      signUpFallbackRedirectUrl={
        import.meta.env.VITE_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL
      }
    >
      <SentryUserTracker />
      <StoreProvider>
        <MantineProvider theme={theme} defaultColorScheme="dark">
          <Notifications position="top-right" />
          <ModalsProvider>
            <BrowserRouter>
              <Suspense
                fallback={
                  <Center h="100vh">
                    <Loader />
                  </Center>
                }
              >
                <Routes>
                  {/* Guest Routes */}
                  <Route
                    path="/"
                    element={<Navigate replace to="/dashboard" />}
                  />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route
                    path="/sign-in"
                    element={<Navigate replace to="/login" />}
                  />
                  <Route
                    path="/sign-up"
                    element={<Navigate replace to="/register" />}
                  />
                  <Route path="/oauthCallback" element={<OAuth />} />
                  <Route path="/connect-channel" element={<ConnectChannel />} />
                  <Route
                    path="/invite/:invitationId"
                    element={<AcceptInvite />}
                  />

                  {/* Protected Routes */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <Dashboard />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contents"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <ContentTable />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contents/:id"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <Content />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/members"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <Member />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/workspaces"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <Workspace />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <Profile />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <Setting />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/billing"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <Setting />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/billing/status"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <Setting />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/inbox"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout>
                          <Inbox />
                        </DashboardLayout>
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </ModalsProvider>
        </MantineProvider>
      </StoreProvider>
    </ClerkProvider>
  );
}

export default App;
